import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { emailService } from '../../../services/emailService';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    serviceCategory: '',
    subject: '',
    message: '',
    preferredContact: '',
    urgency: '',
    agreeTerms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const serviceCategories = [
    { value: 'real-estate', label: 'Immobilier - Location & Vente' },
    { value: 'vehicle-rental', label: 'Location de Véhicules' },
    { value: 'business-consulting', label: 'Conseil aux Entreprises' },
    { value: 'legal-services', label: 'Services Juridiques' },
    { value: 'financial-advisory', label: 'Conseil Financier' },
    { value: 'it-infrastructure', label: 'Infrastructure IT' },
    { value: 'office-supplies', label: 'Fournitures de Bureau' },
    { value: 'event-management', label: 'Gestion d\'Événements' },
    { value: 'security-services', label: 'Services de Sécurité' },
    { value: 'logistics', label: 'Logistique & Transport' },
    { value: 'maintenance', label: 'Maintenance & Entretien' },
    { value: 'other', label: 'Autre Service' }
  ];

  const contactPreferences = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Téléphone' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'in-person', label: 'Rendez-vous en personne' }
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Pas urgent - Dans la semaine' },
    { value: 'medium', label: 'Modéré - Dans 2-3 jours' },
    { value: 'high', label: 'Urgent - Sous 24h' },
    { value: 'critical', label: 'Critique - Immédiat' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!/^\+?[0-9\s-]{10,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    if (!formData?.serviceCategory) {
      newErrors.serviceCategory = 'Veuillez sélectionner une catégorie de service';
    }

    if (!formData?.subject?.trim()) {
      newErrors.subject = 'Le sujet est requis';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData?.message?.trim()?.length < 20) {
      newErrors.message = 'Le message doit contenir au moins 20 caractères';
    }

    if (!formData?.preferredContact) {
      newErrors.preferredContact = 'Veuillez sélectionner un mode de contact préféré';
    }

    if (!formData?.urgency) {
      newErrors.urgency = 'Veuillez indiquer le niveau d\'urgence';
    }

    if (!formData?.agreeTerms) {
      newErrors.agreeTerms = 'Vous devez accepter les conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await emailService.sendContactFormEmail(formData);
      setSubmitSuccess(true);

      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          serviceCategory: '',
          subject: '',
          message: '',
          preferredContact: '',
          urgency: '',
          agreeTerms: false
        });
        setSubmitSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Failed to send email:', err);
      setErrors({
        submit: 'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer ou nous contacter directement par téléphone.'
      });
    } finally {
      setIsSubmitting(false);
    }
    setTimeout(() => {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        serviceCategory: '',
        subject: '',
        message: '',
        preferredContact: '',
        urgency: '',
        agreeTerms: false
      });
      setSubmitSuccess(false);
    }, 3000);
  };

  if (submitSuccess) {
    return (
      <section className="py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>
        <div className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-green-500/10 border-2 border-green-500/30 backdrop-blur-xl rounded-3xl p-10 md:p-16 text-center"
          >
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center mx-auto mb-8 shadow-lg">
              <Icon name="CheckCircle" size={40} color="white" />
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Message Envoyé avec Succès!
            </h3>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">
              Merci de nous avoir contactés. Notre équipe vous répondra dans les plus brefs délais selon votre niveau d'urgence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                iconName="Home"
                iconPosition="left"
                onClick={() => window.location.href = '/homepage'}
                className="bg-blue-600 hover:bg-blue-500 text-white border-none shadow-lg shadow-blue-600/25"
              >
                Retour à l'accueil
              </Button>
              <Button
                variant="outline"
                iconName="MessageSquare"
                iconPosition="left"
                onClick={() => setSubmitSuccess(false)}
                className="border-slate-200 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5"
              >
                Envoyer un autre message
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 relative overflow-hidden">

      {/* Background */}
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-[150px]"
      />
      <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full mb-6">
            <Icon name="Send" size={16} className="text-amber-400" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Formulaire de Contact</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
            Envoyez-nous un <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
              Message
            </span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Remplissez ce formulaire et notre équipe vous contactera rapidement
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <form onSubmit={handleSubmit} className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-8 md:p-12 lg:p-14 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Input
                label="Prénom"
                type="text"
                name="firstName"
                placeholder="Votre prénom"
                value={formData?.firstName}
                onChange={handleChange}
                error={errors?.firstName}
                required
              />

              <Input
                label="Nom"
                type="text"
                name="lastName"
                placeholder="Votre nom"
                value={formData?.lastName}
                onChange={handleChange}
                error={errors?.lastName}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Input
                label="Email"
                type="email"
                name="email"
                placeholder="votre.email@exemple.com"
                value={formData?.email}
                onChange={handleChange}
                error={errors?.email}
                required
              />

              <Input
                label="Téléphone"
                type="tel"
                name="phone"
                placeholder=""
                value={formData?.phone}
                onChange={handleChange}
                error={errors?.phone}
                required
              />
            </div>

            <div className="mb-6">
              <Input
                label="Entreprise (Optionnel)"
                type="text"
                name="company"
                placeholder="Nom de votre entreprise"
                value={formData?.company}
                onChange={handleChange}
              />
            </div>

            <div className="mb-6">
              <Select
                label="Catégorie de Service"
                placeholder="Sélectionnez un service"
                options={serviceCategories}
                value={formData?.serviceCategory}
                onChange={(value) => handleSelectChange('serviceCategory', value)}
                error={errors?.serviceCategory}
                required
                searchable
              />
            </div>

            <div className="mb-6">
              <Input
                label="Sujet"
                type="text"
                name="subject"
                placeholder="Résumé de votre demande"
                value={formData?.subject}
                onChange={handleChange}
                error={errors?.subject}
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
                Message <span className="text-red-400">*</span>
              </label>
              <textarea
                name="message"
                rows="6"
                placeholder="Décrivez votre besoin en détail..."
                value={formData?.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 resize-none backdrop-blur-sm"
              />
              {errors?.message && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors?.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Select
                label="Mode de Contact Préféré"
                placeholder="Comment souhaitez-vous être contacté?"
                options={contactPreferences}
                value={formData?.preferredContact}
                onChange={(value) => handleSelectChange('preferredContact', value)}
                error={errors?.preferredContact}
                required
              />

              <Select
                label="Niveau d'Urgence"
                placeholder="Quelle est l'urgence?"
                options={urgencyLevels}
                value={formData?.urgency}
                onChange={(value) => handleSelectChange('urgency', value)}
                error={errors?.urgency}
                required
              />
            </div>

            <div className="mb-8">
              <Checkbox
                label="J'accepte les conditions d'utilisation et la politique de confidentialité"
                checked={formData?.agreeTerms}
                onChange={handleChange}
                name="agreeTerms"
                error={errors?.agreeTerms}
                required
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {errors?.submit && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 backdrop-blur-sm rounded-xl text-red-400 text-sm flex items-center gap-2">
                  <Icon name="AlertTriangle" size={18} />
                  {errors.submit}
                </div>
              )}

              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-500 text-white border-none shadow-lg shadow-blue-600/25"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer le Message'}
              </Button>

              <Button
                type="button"
                variant="outline"
                fullWidth
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => {
                  setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    company: '',
                    serviceCategory: '',
                    subject: '',
                    message: '',
                    preferredContact: '',
                    urgency: '',
                    agreeTerms: false
                  });
                  setErrors({});
                }}
                className="border-slate-200 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5"
              >
                Réinitialiser
              </Button>
            </div>
          </form>

          {/* Info Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-12 bg-gradient-to-r from-blue-900/40 to-transparent border-l-4 border-amber-500 rounded-r-xl p-8 backdrop-blur-sm"
          >
            <div className="flex items-start gap-5">
              <div className="bg-amber-500/10 p-3 rounded-full flex-shrink-0">
                <Icon name="Info" size={24} className="text-amber-400" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  Temps de Réponse Garanti
                </h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  Nous nous engageons à répondre à toutes les demandes dans les délais suivants :
                  • Critique : Réponse immédiate (dans l'heure)
                  • Urgent : Sous 24 heures
                  • Modéré : Sous 2-3 jours ouvrables
                  • Standard : Sous une semaine
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
